import { ReactNode, useState } from "react";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import StoreReportMirrorError from "./StoreReportMirrorError";

const EMPTY = "—";

type Props =
    {
        mirrorError: string
        isLoading: boolean
        error: string | null
        /** Each report draws its own blocks here*/
        children: (showRaw: boolean) => ReactNode
    }

export default function StoreReportDetails({ mirrorError, isLoading, error, children }: Props) {
    const [showRaw, setShowRaw] = useState(false);
    const loaded = !isLoading && !error;

    return (
        <>
            {!isLoading && (
                <div className="product-details-toolbar">
                    {mirrorError && <StoreReportMirrorError error={mirrorError} />}
                    {loaded && (
                        <div className="product-details-view-toggle">
                            <button
                                type="button"
                                className={showRaw ? "" : "is-active"}
                                onClick={() => setShowRaw(false)}
                                title="Fields"
                                aria-label="Show the fields"
                                aria-pressed={!showRaw}
                            >
                                Aa
                            </button>
                            <button
                                type="button"
                                className={showRaw ? "is-active" : ""}
                                onClick={() => setShowRaw(true)}
                                title="Raw response"
                                aria-label="Show the raw response"
                                aria-pressed={showRaw}
                            >
                                {"</>"}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {isLoading
                ? <div className="activity-log__details-status">Loading details…</div>
                : error
                    ? <div className="activity-log__details-status">{error}</div>
                    : children(showRaw)}
        </>
    );
}

export const DetailFields = ({ entries }: { entries: [string, string][] }) => (
    <dl className="product-details-fields">
        {entries.map(([label, value]) => (
            <div key={label}>
                <dt>{label}</dt>
                <dd>{value || EMPTY}</dd>
            </div>
        ))}
    </dl>
);

type BoxProps =
    {
        title: string
        source: object
        found: boolean | null
        error: string | null
        showRaw: boolean
        children: ReactNode
    }

export const DetailBox = ({ title, source, found, error, showRaw, children }: BoxProps) => {
    const empty = !showRaw && Boolean(error || !found);

    return (
        <div className={`product-details-box ${empty ? "is-empty" : ""}`}>
            <p className="product-details-box__title">{title}</p>
            <div className="product-details-box__body">
                {showRaw
                    ? <pre>{JSON.stringify(source, null, 2)}</pre>
                    : empty
                        ? <p className="product-details-message">
                            <BlockRoundedIcon fontSize="small" />
                            <span>{error || "Not found"}</span>
                          </p>
                        : children}
            </div>
        </div>
    );
};

export const formatDetailDate = (value: string | null) => {
    if (!value) return EMPTY;

    const date = new Date(value);

    return isNaN(date.getTime()) ? EMPTY : date.toLocaleString();
};
